import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.first_name}`);
      nav(u.role === "admin" ? "/app/admin" : "/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8" data-testid="login-logo-link">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-purple-400 to-purple-600 grid place-items-center"><ShieldCheck size={18} className="text-white"/></div>
          <span className="font-semibold text-slate-900 text-lg">Informed <span className="text-purple-600">Accelerator</span></span>
        </Link>
        <Card className="glass rounded-2xl">
          <CardContent className="p-8">
            <div className="eyebrow">Sign in</div>
            <h1 className="text-2xl font-semibold text-slate-900 mt-1">Welcome back</h1>
            <p className="text-sm text-slate-500 mt-1">Enter your credentials to continue.</p>
            <form className="mt-6 space-y-4" onSubmit={submit}>
              <div>
                <Label htmlFor="email" className="text-slate-700">Email</Label>
                <Input id="email" type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} data-testid="login-email-input" className="mt-1 bg-slate-50 border-slate-200 text-slate-900" />
              </div>
              <div>
                <Label htmlFor="password" className="text-slate-700">Password</Label>
                <Input id="password" type="password" required value={password} onChange={(e)=>setPassword(e.target.value)} data-testid="login-password-input" className="mt-1 bg-slate-50 border-slate-200 text-slate-900" />
              </div>
              <Button type="submit" disabled={loading} data-testid="login-submit-button" className="btn-primary rounded-xl w-full py-6">
                {loading ? "Signing in..." : "Sign in"}
              </Button>
            </form>
            <div className="mt-4 text-sm text-slate-500 text-center">
              <Link to="/forgot-password" className="text-purple-600 hover:underline" data-testid="login-forgot-link">Forgot password?</Link>
            </div>
            <div className="mt-2 text-sm text-slate-500 text-center">
              No account? <Link to="/signup" className="text-purple-600 hover:underline" data-testid="login-to-signup-link">Create one</Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
